// =============================================================================
// ATS BOARD REGISTRY + RESOLUTION COST-METER — the career-page-resolver
// governance spine (Wave-0 / F2). Shape only — NO feeders, NO resolver logic.
//
// `ats_board_registry`: the durable, shared (no userId — a board mapping is a
// fact about the world), self-healing company -> ATS map. Keyed by a normalized
// company_key (the DB dedup key AND the future KV cache key) so a mapping is
// stored once and re-resolution upserts in place (ON CONFLICT (company_key)),
// mirroring platform_algorithm_rules.rule_key. `source` ranks trust
// harvested < discovered < learned < manual, with MANUAL SOVEREIGN — a learned
// re-probe must never clobber a user correction (guard upserts WHERE
// source <> 'manual').
//
// `ats_resolution_attempts`: append-only cost meter — the role ai_call_log plays
// for Claude and cron_runs for the scheduler (mirrors operations.ts). One row per
// resolve attempt; roll-ups (hit/miss/probe rate, p95 latency, which layer served)
// come straight out of this and GATE whether more ATS adapters earn their build.
// "Never silence the cost meter."
//
// Additive only (rule #11). Mirrors platform_algorithm.ts conventions.
// =============================================================================

import { sql } from 'drizzle-orm';
import {
  boolean,
  index,
  integer,
  jsonb,
  pgTable,
  text,
  timestamp,
  varchar,
} from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

/** Provenance for a registry mapping: where the company -> board answer came from. */
export interface AtsRegistrySource {
  /** 'harvested' (mined from an ingested job / watchlist slug), 'discovered'
   *  (proactively resolved from a corpus target), 'learned' (confirmed during a
   *  live resolve), 'adjacency-inferred' (v2). */
  via: string;
  /** Optional signal that produced it (e.g. the through-line that suggested it). */
  signal?: string;
  url?: string;
  retrievedAt?: string; // ISO
}

export const atsBoardRegistry = pgTable(
  'ats_board_registry',
  {
    id: id(),
    // Normalized company slug — the DB dedup key AND the KV cache key. UNIQUE
    // enforces store-once + lets a re-resolution upsert in place.
    companyKey: varchar('company_key', { length: 200 }).notNull().unique(),
    // The display name as last seen.
    companyName: varchar('company_name', { length: 200 }).notNull(),
    // greenhouse | lever | ashby | workday | smartrecruiters | recruitee | generic.
    ats: varchar('ats', { length: 24 }).notNull(),
    // Clean-subdomain slug (Greenhouse / Lever / Ashby / Recruitee).
    boardSlug: varchar('board_slug', { length: 200 }),
    // Workday tenant / region (wd{N}) / site path — forward-compat, deferred.
    tenant: varchar('tenant', { length: 120 }),
    region: varchar('region', { length: 16 }),
    site: varchar('site', { length: 120 }),
    // Captured human careers/embed URL when there is no clean slug.
    careersUrl: text('careers_url'),
    // 0-100 (matches platform_algorithm.ts; NOT a float).
    confidence: integer('confidence'),
    // harvested | discovered | learned | manual — MANUAL SOVEREIGN.
    source: varchar('source', { length: 16 }).notNull().default('harvested'),
    // Provenance trail (via / signal / url / retrievedAt).
    sources: jsonb('sources').$type<AtsRegistrySource[]>().default(sql`'[]'::jsonb`),
    // seed | verified | stale.
    verifyState: varchar('verify_state', { length: 16 }).notNull().default('seed'),
    lastVerifiedAt: timestamp('last_verified_at', { withTimezone: true }),
    // Consecutive 404s; trips invalidate + re-resolve (ATS-migration self-heal).
    lastPoll404: integer('last_poll_404').notNull().default(0),
    ...timestamps,
  },
  (t) => ({
    atsIdx: index('ats_board_registry_ats_idx').on(t.ats),
  }),
);

/** Losing candidates on a near-tie — surfaced for the audit + a confirm-card. */
export interface AtsRejectedAlternative {
  url: string;
  title: string;
}

export const atsResolutionAttempts = pgTable(
  'ats_resolution_attempts',
  {
    id: id(),
    // The normalized key attempted (nullable — a bad/empty hint may have none).
    companyKey: varchar('company_key', { length: 200 }),
    companyName: varchar('company_name', { length: 200 }),
    // The paste-only listing URL (context only; never fetched).
    inputUrl: text('input_url'),
    // The ats / URL that served the answer; null on miss.
    resolvedAts: varchar('resolved_ats', { length: 24 }),
    resolvedUrl: text('resolved_url'),
    // Which layer served: kv | registry | probe | search | none.
    cacheLayer: varchar('cache_layer', { length: 12 }).notNull(),
    // hit | miss | error.
    outcome: varchar('outcome', { length: 12 }).notNull(),
    confidence: integer('confidence'),
    // 0-100 char similarity / token-set of the chosen title match.
    titleSim: integer('title_sim'),
    titleToken: integer('title_token'),
    seniorityMatch: boolean('seniority_match'),
    locationMatch: boolean('location_match'),
    // same | different | unsure | skipped (the pairwise verifier).
    llmVerdict: varchar('llm_verdict', { length: 12 }),
    rejectedAlternatives: jsonb('rejected_alternatives').$type<AtsRejectedAlternative[]>(),
    durationMs: integer('duration_ms'),
    errorMessage: text('error_message'),
    ...timestamps,
  },
  (t) => ({
    companyKeyIdx: index('ats_resolution_attempts_company_key_idx').on(t.companyKey),
    outcomeIdx: index('ats_resolution_attempts_outcome_idx').on(t.outcome),
  }),
);
